import { useEffect, useState } from "react";
import { User, Buildings } from "@phosphor-icons/react";
import api from "@/lib/api";
import { CountrySelect } from "@/pages/GuestsPage";

export const EMPTY_COMPANY = { id: null, company_name: "", company_address: "", company_postal_code: "", company_city: "", company_country: "", company_oib: "" };

function Field({ label, children }) {
  return <label className="block"><span className="mb-1 block text-xs font-medium text-foreground">{label}</span>{children}</label>;
}

function ModeSwitch({ mode, setMode }) {
  const b = (m, Icon, l) => (
    <button type="button" onClick={() => setMode(m)} className={`flex flex-1 items-center justify-center gap-1.5 px-3 py-1.5 ${mode === m ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-muted"}`} data-testid={`res-billing-${m}`}>
      <Icon size={13} weight="bold" /> {l}
    </button>
  );
  return <div className="flex overflow-hidden rounded-md border border-border text-xs font-semibold" data-testid="res-billing-switch">{b("person", User, "Fizička osoba")}{b("company", Buildings, "Tvrtka")}</div>;
}

function CompanyPicker({ company, setCompany }) {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/guests", { params: { is_company: true, limit: 500 } }).then(({ data }) => setList(Array.isArray(data) ? data : data?.items || [])).catch(() => {}); }, []);
  const pick = (id) => {
    if (!id) return setCompany({ ...EMPTY_COMPANY });
    const c = list.find((x) => x.id === id);
    if (c) setCompany({ id: c.id, ...Object.fromEntries(Object.keys(EMPTY_COMPANY).filter((k) => k !== "id").map((k) => [k, c[k] || ""])) });
  };
  return (
    <select className="rw-input" value={company.id || ""} onChange={(e) => pick(e.target.value)} data-testid="res-company-select">
      <option value="">+ Nova tvrtka</option>
      {list.map((c) => <option key={c.id} value={c.id}>{c.company_name || c.first_name}{c.company_oib ? ` · ${c.company_oib}` : ""}</option>)}
    </select>
  );
}

function CompanyBlock({ company, setCompany, guestName, setGuestName }) {
  const set = (k) => (e) => setCompany((c) => ({ ...c, [k]: e?.target ? e.target.value : e }));
  return (
    <div className="space-y-3 rounded-lg border border-border p-3" data-testid="res-company-block">
      <Field label="Odaberi postojeću tvrtku"><CompanyPicker company={company} setCompany={setCompany} /></Field>
      {!company.id && <p className="rounded bg-amber-500/10 px-2 py-1 text-[11px] font-medium text-amber-700 dark:text-amber-300" data-testid="res-company-new-hint">Unosite NOVU tvrtku — spremit će se u Gosti/Tvrtke.</p>}
      <Field label="Naziv tvrtke *"><input className="rw-input" value={company.company_name} onChange={set("company_name")} data-testid="res-company-name" /></Field>
      <Field label="Adresa"><input className="rw-input" value={company.company_address} onChange={set("company_address")} data-testid="res-company-address" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Poštanski broj"><input className="rw-input" value={company.company_postal_code} onChange={set("company_postal_code")} data-testid="res-company-postal" /></Field>
        <Field label="Grad"><input className="rw-input" value={company.company_city} onChange={set("company_city")} data-testid="res-company-city" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Država"><CountrySelect className="rw-input" value={company.company_country} onChange={set("company_country")} testId="res-company-country" /></Field>
        <Field label="OIB tvrtke *"><input className="rw-input" value={company.company_oib} onChange={set("company_oib")} data-testid="res-company-oib" /></Field>
      </div>
      <Field label="Ime gosta (neobavezno — ispisuje se na računu)">
        <input className="rw-input" value={guestName || ""} onChange={(e) => setGuestName(e.target.value)} placeholder="Ostavite prazno ako tvrtka ne traži" data-testid="res-invoice-guest-name" />
      </Field>
    </div>
  );
}

function PersonBlock({ form, update, guestAddress, setGuestAddress, reservation, onOpenProfile }) {
  const setA = (k) => (e) => setGuestAddress((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  return (
    <>
      <div>
        <div className="mb-1 flex items-center justify-between gap-2">
          <span className="text-xs font-medium text-foreground">Ime i prezime gosta</span>
          {reservation?.primary_guest_id && (
            <button type="button" onClick={() => onOpenProfile(reservation.primary_guest_id)} className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-0.5 text-[10px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground" data-testid="res-open-guest-profile" title="Otvori profil gosta u tabu Gosti/Tvrtke">
              <User size={11} weight="bold" /> Profil gosta
            </button>
          )}
        </div>
        <input className="rw-input" value={form.primary_guest_name} onChange={(e) => update("primary_guest_name", e.target.value)} placeholder="npr. Marko Marković" data-testid="res-guest-name" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Email"><input type="email" className="rw-input" value={form.primary_guest_email || ""} onChange={(e) => update("primary_guest_email", e.target.value)} data-testid="res-guest-email" /></Field>
        <Field label="Telefon"><input className="rw-input" value={form.primary_guest_phone || ""} onChange={(e) => update("primary_guest_phone", e.target.value)} data-testid="res-guest-phone" /></Field>
      </div>
      <div className="space-y-3 rounded-lg border border-border p-3" data-testid="res-guest-address-block">
        <div className="flex items-start justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Profil gosta</span>
          {reservation?.primary_guest_id && <span className="text-right text-[10.5px] leading-tight text-muted-foreground">Sinkronizirano s Gosti/Tvrtke</span>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Email (profil gosta)"><input type="email" className="rw-input" value={guestAddress.email} onChange={setA("email")} data-testid="res-guest-profile-email" /></Field>
          <Field label="Telefon (profil gosta)"><input className="rw-input" value={guestAddress.phone} onChange={setA("phone")} data-testid="res-guest-profile-phone" /></Field>
        </div>
        <Field label="Adresa"><input className="rw-input" value={guestAddress.address} onChange={setA("address")} placeholder="Ulica i kućni broj" data-testid="res-guest-address" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Poštanski broj"><input className="rw-input" value={guestAddress.postal_code} onChange={setA("postal_code")} data-testid="res-guest-postal" /></Field>
          <Field label="Grad"><input className="rw-input" value={guestAddress.city} onChange={setA("city")} data-testid="res-guest-city" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Država"><CountrySelect className="rw-input" value={guestAddress.country} onChange={setA("country")} testId="res-guest-country" /></Field>
          <Field label="OIB (neobavezno)"><input className="rw-input" value={guestAddress.oib} onChange={setA("oib")} data-testid="res-guest-oib" /></Field>
        </div>
      </div>
    </>
  );
}

/** Reservation → Detalji: who is billed — a person (guest profile) or a company (Gosti/Tvrtke). */
export default function BillingPartySection({ mode, setMode, company, setCompany, form, update, guestAddress, setGuestAddress, reservation, onOpenProfile }) {
  return (
    <div className="space-y-3">
      <ModeSwitch mode={mode} setMode={setMode} />
      {mode === "company" ? (
        <CompanyBlock company={company} setCompany={setCompany} guestName={form.invoice_guest_name} setGuestName={(v) => update("invoice_guest_name", v)} />
      ) : (
        <PersonBlock form={form} update={update} guestAddress={guestAddress} setGuestAddress={setGuestAddress} reservation={reservation} onOpenProfile={onOpenProfile} />
      )}
    </div>
  );
}

/** Create or update the company profile; returns its id. */
export async function saveCompany(company) {
  const body = { ...company, first_name: company.company_name, last_name: "", is_company: true };
  delete body.id;
  Object.keys(body).forEach((k) => { if (body[k] === "") body[k] = k === "last_name" ? "" : null; });
  if (company.id) { await api.put(`/guests/${company.id}`, body); return company.id; }
  const { data } = await api.post("/guests", body);
  return data.id;
}
